import React, { useEffect, useRef, useState } from "react";
import {
    FiPlus, FiEdit2, FiTrash2, FiMail, FiPhone, FiCreditCard,
    FiSearch, FiX, FiClock, FiLogIn, FiLogOut, FiShield,
    FiPackage, FiLink, FiUsers, FiTrendingUp, FiUser, FiCalendar,
    FiChevronRight
} from "react-icons/fi";
import axiosInstance from "../utils/api";
import toast from "react-hot-toast";
import StandisteModal from "./standisteModal";
import DeleteModal from "./deleteModal";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";

const TYPE_MAP = {
    build_up: ["build_up", "buildup", "build-up", "buildUp", "Build Up"],
    show_days: ["show_days", "showdays", "show-days", "showDays", "Show Days"],
    teardown: ["teardown", "tear-down", "tearDown", "Teardown"],
    full_access: ["full_access", "full-access", "fullAccess", "FullAccess"],
};

const StandistesTab = ({ eventId, isSubAdmin }) => {
    const navigate = useNavigate();
    const socketRef = useRef(null);
    const [standistes, setStandistes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [detailsModal, setDetailsModal] = useState(null);
    const [loadingBadges, setLoadingBadges] = useState(false);
    const [activeTab, setActiveTab] = useState("build_up");
    const [eventDatesModal, setEventDatesModal] = useState(false);
    const [eventData, setEventData] = useState(null);
    const [newBadgesMap, setNewBadgesMap] = useState({});
    const [eventDates, setEventDates] = useState({
        buildUp: { start: "", end: "" },
        showDays: { start: "", end: "" },
        teardown: { start: "", end: "" },
    });

    const fetchInitialData = async () => {
        try {
            setLoading(true);

            const [standistesRes, eventRes] = await Promise.all([
                axiosInstance.get(`/auth/standistes/${eventId}`),
                axiosInstance.get(`/event/${eventId}`)
            ]);

            const standistesData = standistesRes.data.standistes || [];
            setStandistes(standistesData);
            setEventData(eventRes.data.event);

            const badgeResults = await Promise.all(
                standistesData.map(s =>
                    axiosInstance.get(`/badge/by-standiste/${s._id}`)
                        .then(({ data }) => {
                            const badges = Array.isArray(data) ? data : data.badges || [];
                            return { id: s._id, hasNew: badges.some(b => b.new === true) };
                        })
                        .catch(() => ({ id: s._id, hasNew: false }))
                )
            );

            const map = {};
            badgeResults.forEach(({ id, hasNew }) => {
                map[id] = hasNew;
            });
            setNewBadgesMap(map);

        } catch (e) {
            toast.error("Failed to load event data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, [eventId]);

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        try {
            setDeleting(true);
            await axiosInstance.delete(`/auth/user/${deleteTarget._id}`);
            toast.success("Standiste deleted");
            setDeleteTarget(null);
            fetchInitialData();
        } catch {
            toast.error("Failed to delete standiste");
        } finally {
            setDeleting(false);
        }
    };

    const handleCopyGlobalLink = () => {
        navigator.clipboard.writeText(`${window.location.origin}/${eventId}`);
        toast.success("Global registration link copied!");
    };

    const openEventDatesModal = () => {
        if (!eventData) return;

        setEventDates({
            buildUp: {
                start: eventData.buildUp?.start?.slice(0, 16) || "",
                end: eventData.buildUp?.end?.slice(0, 16) || "",
            },
            showDays: {
                start: eventData.showDays?.start?.slice(0, 16) || "",
                end: eventData.showDays?.end?.slice(0, 16) || "",
            },
            teardown: {
                start: eventData.teardown?.start?.slice(0, 16) || "",
                end: eventData.teardown?.end?.slice(0, 16) || "",
            },
        });

        setEventDatesModal(true);
    };

    const handleUpdateEventDates = async () => {
        try {
            const { data } = await axiosInstance.put(`/event/${eventId}`, {
                buildUp: eventDates.buildUp,
                showDays: eventDates.showDays,
                teardown: eventDates.teardown,
            });

            setEventData(data.data);
            toast.success("Event dates updated successfully");
            setEventDatesModal(false);
        } catch {
            toast.error("Failed to update event dates");
        }
    };

    const handleCardClick = async (standiste) => {
        try {
            setLoadingBadges(true);
            setDetailsModal({ ...standiste, badges: [] });
            setActiveTab("build_up");

            if (socketRef.current?.connected) {
                socketRef.current.emit("join-chef-room", standiste._id);
            }

            const { data: standisteData } = await axiosInstance.get(`/badge/by-standiste/${standiste._id}`);
            const standisteBadges = Array.isArray(standisteData) ? standisteData : standisteData.badges || [];

            const newBadges = standisteBadges.filter(b => b.new === true);
            if (newBadges.length > 0) {
                await Promise.all(
                    newBadges.map(b => axiosInstance.put(`/badge/${b._id}`, { new: false }))
                );
                setNewBadgesMap(prev => ({ ...prev, [standiste._id]: false }));
            }

            const { data: allData } = await axiosInstance.get(`/badge/by-chef/${standiste._id}`);
            const badgesArray = Array.isArray(allData) ? allData : allData.badges || [];

            const newBadgeIds = new Set(newBadges.map(b => b._id));
            badgesArray.forEach(b => { if (newBadgeIds.has(b._id)) b.new = false; });

            const tabOrder = ["build_up", "show_days", "teardown"];
            const firstActiveTab = tabOrder.find(t =>
                badgesArray.some(b => (TYPE_MAP[t] || [t]).includes(b.type))
            );
            if (firstActiveTab) setActiveTab(firstActiveTab);

            setDetailsModal({ ...standiste, badges: badgesArray });
        } catch (e) {
            toast.error("Failed to load badge information");
            setDetailsModal({ ...standiste, badges: [] });
        } finally {
            setLoadingBadges(false);
        }
    };

    useEffect(() => {
        const socket = io("https://api.expo-pilot.com");
        socketRef.current = socket;

        socket.on("connect", () => {
            console.log("Connected to socket:", socket.id);
        });

        socket.on("badge-scanned", async (data) => {
            setDetailsModal(prev => {
                if (!prev || prev._id !== data.chefId) return prev;

                setLoadingBadges(true);
                axiosInstance.get(`/badge/by-chef/${data.chefId}`)
                    .then(({ data: res }) => {
                        const badgesArray = Array.isArray(res) ? res : res.badges || [];
                        setDetailsModal(current => {
                            if (!current || current._id !== data.chefId) return current;
                            return { ...current, badges: badgesArray };
                        });
                    })
                    .catch(() => {
                        toast.error("Failed to refresh — showing last known data");
                    })
                    .finally(() => setLoadingBadges(false));

                return { ...prev };
            });
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    const filteredStandistes = standistes.filter(s => {
        const q = searchQuery.toLowerCase();
        return s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) || s.cin?.toLowerCase().includes(q);
    });

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    const getBadgesByType = (badges, type) => {
        if (!Array.isArray(badges)) return badges;
        return badges;
    };

    const getOnsiteCount = (badges, type) =>
        getBadgesByType(badges, type).filter(badge => {
            if (!badge.scanLogs?.length) return false;
            const latest = [...badge.scanLogs].sort((a, b) => new Date(b.scannedAt) - new Date(a.scannedAt))[0];
            return latest.action === 'entry';
        }).length;

    const getScanLogsByType = (badges, type) =>
        getBadgesByType(badges, type)
            .flatMap(badge =>
                (badge.scanLogs || []).map(log => ({
                    ...log,
                    holderName:
                        badge.name ||
                        badge.standiste?.name ||
                        badge.chef?.name ||
                        "—",
                    holderCin: badge.cin || null,
                }))
            )
            .sort((a, b) => new Date(b.scannedAt) - new Date(a.scannedAt));


    const ScanHistoryTab = ({ type, badges, isLoading }) => {
        const typeBadges = getBadgesByType(badges, type);
        const total = typeBadges.length;
        const onsite = getOnsiteCount(badges, type);
        const offsite = total - onsite;
        const logs = getScanLogsByType(badges, type);
        const entries = logs.filter(l => l.action === 'entry').length;
        const exits = logs.filter(l => l.action === 'exit').length;

        const color =
            type === "build_up"
                ? {
                    light: "bg-blue-50",
                    border: "border-blue-200",
                    text: "text-blue-700",
                    pill: "bg-blue-100 text-blue-700",
                    spin: "border-blue-500",
                }
                : type === "show_days"
                    ? {
                        light: "bg-yellow-50",
                        border: "border-yellow-200",
                        text: "text-yellow-700",
                        pill: "bg-green-100 text-yellow-700",
                        spin: "border-yellow-500",
                    }
                    : {
                        light: "bg-purple-50",
                        border: "border-purple-200",
                        text: "text-purple-700",
                        pill: "bg-purple-100 text-purple-700",
                        spin: "border-purple-500",
                    };

        if (isLoading) {
            return (
                <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                        <div className={`w-6 h-6 border-2 ${color.spin} border-t-transparent rounded-full animate-spin mx-auto mb-2`} />
                        <span className="text-xs text-slate-400">Loading badge data...</span>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-4">

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className={`rounded-lg p-3 ${color.light} border ${color.border}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiPackage className={color.text} size={11} />
                            <span className={`text-[10px] font-semibold ${color.text}`}>Total Badges</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{total}</div>
                    </div>
                    <div className="rounded-lg p-3 bg-green-50 border border-green-200">
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiLogIn className="text-green-600" size={11} />
                            <span className="text-[10px] font-semibold text-green-700">Onsite</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{onsite}</div>
                    </div>
                    <div className="rounded-lg p-3 bg-slate-50 border border-slate-200">
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiLogOut className="text-slate-500" size={11} />
                            <span className="text-[10px] font-semibold text-slate-600">Offsite</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{offsite}</div>
                    </div>
                    <div className="rounded-lg p-3 bg-orange-50 border border-orange-200">
                        <div className="flex items-center gap-1.5 mb-1">
                            <FiTrendingUp className="text-orange-500" size={11} />
                            <span className="text-[10px] font-semibold text-orange-600">Total Scans</span>
                        </div>
                        <div className="text-2xl font-bold text-slate-800">{logs.length}</div>
                    </div>
                </div>

                {logs.length > 0 && (
                    <div className="flex gap-2">
                        <div className="flex-1 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                            <FiLogIn className="text-green-600 flex-shrink-0" size={13} />
                            <span className="text-xs font-medium text-green-700">{entries} Entries</span>
                        </div>
                        <div className="flex-1 flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                            <FiLogOut className="text-orange-500 flex-shrink-0" size={13} />
                            <span className="text-xs font-medium text-orange-600">{exits} Exits</span>
                        </div>
                    </div>
                )}

                <div>
                    <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                        <FiClock size={12} /> All Scan Activity
                    </h4>

                    {logs.length === 0 ? (
                        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                            <FiClock size={22} className="mx-auto mb-2 text-slate-300" />
                            <p className="text-xs text-slate-400">No scans recorded for {type} badges</p>
                        </div>
                    ) : (
                        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-0.5">
                            {logs.map((log, idx) => (
                                <div
                                    key={idx}
                                    className={`flex items-center justify-between p-2.5 rounded-md border-l-4 ${log.action === 'entry'
                                        ? 'bg-green-50 border-green-500'
                                        : 'bg-orange-50 border-orange-400'
                                        }`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {log.action === 'entry'
                                            ? <FiLogIn className="text-green-600 flex-shrink-0" size={14} />
                                            : <FiLogOut className="text-orange-500 flex-shrink-0" size={14} />
                                        }
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-xs font-semibold text-slate-800 capitalize">{log.action}</span>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${color.pill}`}>{type}</span>
                                            </div>
                                            <div className="flex items-center gap-1 mt-0.5">
                                                <FiUser size={10} className="text-slate-400 flex-shrink-0" />
                                                <span className="text-[11px] text-slate-600 truncate">{log.holderName}</span>
                                                {log.holderCin && (
                                                    <span className="text-[10px] text-slate-400 flex-shrink-0">· {log.holderCin}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-3">
                                        <div className="flex items-center justify-end gap-1 text-xs text-slate-700 font-medium">
                                            <FiShield size={10} className="text-slate-400" />
                                            {log.gate}
                                        </div>
                                        <div className="text-[10px] text-slate-400 mt-0.5">{formatDate(log.scannedAt)}</div>
                                        {log.agent?.name && (
                                            <div className="text-[10px] text-slate-400">by {log.agent.name}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const ProfileAvatar = ({ user, size = "md" }) => {
        const sz = { sm: "w-10 h-10 text-xs", md: "w-12 h-12 text-sm", lg: "w-14 h-14 text-base" };
        return (
            <div className={`${sz[size]} rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-2 border-white shadow-sm ring-2 ring-slate-100 flex-shrink-0`}>
                <span className="font-bold text-white">{user.name?.charAt(0)?.toUpperCase() || '?'}</span>
            </div>
        );
    };

    if (loading) return <div className="py-6 text-center text-xs text-slate-500">Loading...</div>;

    const dateSectionConfig = [
        { key: "buildUp", label: "Build Up", accent: "bg-blue-600", light: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" },
        { key: "showDays", label: "Show Days", accent: "bg-amber-500", light: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-400" },
        { key: "teardown", label: "Teardown", accent: "bg-violet-600", light: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500" },
    ];

    return (
        <div className="space-y-3">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <h2 className="text-lg font-bold text-slate-800">Stand builders</h2>
                <div className="flex items-center gap-2">
                    <button onClick={handleCopyGlobalLink} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded-md text-xs font-medium transition shadow-sm">
                        <FiLink size={13} /> Copy Registration Link
                    </button>
                </div>
            </div>

            <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <input
                    type="text"
                    placeholder="Search by name, email, or CIN..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        <FiX size={14} />
                    </button>
                )}
            </div>

            {!filteredStandistes.length ? (
                <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-lg text-xs text-slate-500">
                    {searchQuery ? "No stand builders match your search" : "No stand builders yet"}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {filteredStandistes.map((s) => (
                        <div
                            key={s._id}
                            onClick={() => handleCardClick(s)}
                            className="relative bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
                        >
                            {newBadgesMap[s._id] && (
                                <div className="absolute top-2 right-2 flex items-center gap-1 bg-emerald-50 border border-emerald-300 text-emerald-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                                    New
                                </div>
                            )}
                            <div className="flex items-start gap-3 mb-2">
                                <ProfileAvatar user={s} size="sm" />
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-sm text-slate-800 group-hover:text-blue-600 transition truncate">{s.name}</h3>
                                    <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-semibold rounded-full mt-1">Stand builder</span>
                                </div>
                            </div>
                            <div className="space-y-1.5 text-xs text-slate-600 mb-2">
                                <div className="flex items-center gap-2"><FiMail className="text-slate-400 flex-shrink-0" size={12} /><span className="truncate">{s.email}</span></div>
                                {s.cin && <div className="flex items-center gap-2"><FiCreditCard className="text-slate-400 flex-shrink-0" size={12} /><span className="truncate">{s.cin}</span></div>}
                            </div>
                            {!isSubAdmin && <div className="flex gap-1.5 pt-2 border-t border-slate-100">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setSelected(s); setOpen(true); }}
                                    className="flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded-md text-slate-700 hover:bg-slate-100 font-medium transition"
                                >
                                    <FiEdit2 size={12} /> Edit
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(s); }}
                                    className="flex-1 flex items-center justify-center gap-1 text-[11px] py-1.5 rounded-md text-red-600 hover:bg-red-50 font-medium transition"
                                >
                                    <FiTrash2 size={12} /> Delete
                                </button>
                            </div>}
                        </div>
                    ))}
                </div>
            )}

            {detailsModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl">

                        <div className="p-4 border-b border-slate-200 flex-shrink-0">

                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <ProfileAvatar user={detailsModal} size="lg" />
                                    <div className="min-w-0">
                                        <h3 className="text-base font-bold text-slate-800 truncate">{detailsModal.name}</h3>
                                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                                            <span className="flex items-center gap-1"><FiMail size={10} />{detailsModal.email}</span>
                                            {detailsModal.cin && <span className="flex items-center gap-1"><FiCreditCard size={10} />{detailsModal.cin}</span>}
                                            {detailsModal.phone && <span className="flex items-center gap-1"><FiPhone size={10} />{detailsModal.phone}</span>}
                                        </div>
                                    </div>
                                </div>
                                <button onClick={() => setDetailsModal(null)} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
                                    <FiX size={18} />
                                </button>
                            </div>

                            {!isSubAdmin && <div className="flex gap-2 mb-4">
                                <button
                                    onClick={() => navigate(`/event/${eventId}/${detailsModal._id}`)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition shadow-sm"
                                >
                                    <FiUsers size={13} /> View Staff
                                </button>
                            </div>}

                            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                                {[
                                    { key: "build_up", label: "Build Up" },
                                    { key: "show_days", label: "Show Days" },
                                    { key: "teardown", label: "Teardown" },
                                ].map(tab => (
                                    <button
                                        key={tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-semibold transition ${activeTab === tab.key
                                            ? `bg-white ${tab.activeClass} shadow-sm`
                                            : "text-slate-500 hover:text-slate-700"
                                            }`}
                                    >
                                        <FiClock size={11} />
                                        {tab.label}
                                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${activeTab === tab.key ? tab.pillClass : "bg-slate-200 text-slate-500"
                                            }`}>
                                            {getScanLogsByType(detailsModal.badges, tab.key).length}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4">
                            <ScanHistoryTab
                                type={activeTab}
                                badges={detailsModal.badges}
                                isLoading={loadingBadges}
                            />
                        </div>

                        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 flex-shrink-0">
                            <button
                                onClick={() => setDetailsModal(null)}
                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium transition"
                            >
                                Close
                            </button>
                            {!isSubAdmin && <button
                                onClick={() => { setSelected(detailsModal); setDetailsModal(null); setOpen(true); }}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-medium transition flex items-center gap-1.5"
                            >
                                <FiEdit2 size={12} /> Edit Stand builder
                            </button>}
                        </div>
                    </div>
                </div>
            )}

            <DeleteModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDeleteConfirm}
                isDeleting={deleting}
                title="Delete Stand builder"
                message={`Are you sure you want to delete ${deleteTarget?.name}? This action cannot be undone.`}
            />

            <StandisteModal
                isOpen={open}
                onSuccess={fetchInitialData}
                onClose={() => setOpen(false)}
                eventId={eventId}
                standiste={selected}
            />

            {eventDatesModal && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">

                        <div
                            className="px-5 py-4 flex items-center justify-between"
                            style={{ background: "linear-gradient(135deg, #0f2042 0%, #1a3a6e 100%)" }}
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-white bg-opacity-15 flex items-center justify-center">
                                    <FiCalendar size={15} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-white leading-tight">Event Dates</h3>
                                    <p className="text-[11px] text-blue-200 mt-0.5">Configure timeline phases</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setEventDatesModal(false)}
                                className="w-7 h-7 rounded-lg bg-white bg-opacity-10 hover:bg-opacity-20 flex items-center justify-center transition text-white"
                            >
                                <FiX size={14} />
                            </button>
                        </div>

                        <div className="p-5 space-y-3 bg-slate-50">
                            {dateSectionConfig.map(({ key, label, light, border, text, dot }) => (
                                <div key={key} className={`rounded-xl border ${border} ${light} overflow-hidden`}>

                                    <div className={`px-4 py-2 flex items-center gap-2 border-b ${border}`}>
                                        <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
                                        <span className={`text-[11px] font-bold uppercase tracking-wide ${text}`}>{label}</span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3 p-3">
                                        <div>
                                            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Start</label>
                                            <input
                                                type="datetime-local"
                                                value={eventDates[key].start}
                                                onChange={(e) =>
                                                    setEventDates({
                                                        ...eventDates,
                                                        [key]: { ...eventDates[key], start: e.target.value },
                                                    })
                                                }
                                                className="w-full border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent shadow-sm"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">End</label>
                                            <input
                                                type="datetime-local"
                                                value={eventDates[key].end}
                                                onChange={(e) =>
                                                    setEventDates({
                                                        ...eventDates,
                                                        [key]: { ...eventDates[key], end: e.target.value },
                                                    })
                                                }
                                                className="w-full border border-slate-200 bg-white rounded-lg px-2.5 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent shadow-sm"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="px-5 py-3 bg-white border-t border-slate-200 flex justify-end gap-2">
                            <button
                                onClick={() => setEventDatesModal(false)}
                                className="px-4 py-1.5 rounded-lg text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateEventDates}
                                className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white transition shadow-sm flex items-center gap-1.5"
                                style={{ background: "linear-gradient(135deg, #0f2042 0%, #1a3a6e 100%)" }}
                                onMouseEnter={e => e.currentTarget.style.background = "linear-gradient(135deg, #0a1a35 0%, #163060 100%)"}
                                onMouseLeave={e => e.currentTarget.style.background = "linear-gradient(135deg, #0f2042 0%, #1a3a6e 100%)"}
                            >
                                <FiCalendar size={11} />
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StandistesTab;